import { DISCIPLINES, type Discipline, type Mastery, masteriesOf, type PassiveEffect, skill, SKILLS, type SkillId, type Stat } from '@wyn/content';
import { balance } from './config/balance.ts';
import type { GameEvent, PlayerState } from './player.ts';
import { skillProgress } from './progression.ts';

/** Mastery tier reached in each Discipline (0–4), as stored on the player. Missing means 0. */
export type MasteryTiers = Partial<Record<Discipline, number>>;
/** Total XP per skill, as the skill rows hold it. Missing means none. */
export type SkillXp = Partial<Record<SkillId, number>>;

/** Sum of the four skill levels in each Discipline. Every skill starts at 1, so the floor is 4. */
export function disciplineLevels(xp: SkillXp): Record<Discipline, number> {
  const out = Object.fromEntries(DISCIPLINES.map((d) => [d, 0])) as Record<Discipline, number>;
  for (const id of SKILLS) out[skill(id).discipline] += skillProgress(xp[id] ?? 0).level;
  return out;
}

/** The tier each Discipline has reached from its total levels. Disciplines at tier 0 are left out. */
export function masteryTiers(xp: SkillXp): MasteryTiers {
  const levels = disciplineLevels(xp);
  const out: MasteryTiers = {};
  for (const d of DISCIPLINES) {
    const tier = masteriesOf(d).filter((m) => levels[d] >= m.levels).length;
    if (tier > 0) out[d] = tier;
  }
  return out;
}

/** XP never goes down, so neither does a tier: keep the higher of what is stored and what was computed. */
export function raiseTiers(stored: MasteryTiers | undefined, computed: MasteryTiers): MasteryTiers {
  const out: MasteryTiers = { ...stored };
  for (const d of DISCIPLINES) if ((computed[d] ?? 0) > (out[d] ?? 0)) out[d] = computed[d];
  return out;
}

/** Every title earned, lowest tier first within each Discipline. */
export function earned(p: Pick<PlayerState, 'mastery'>): Mastery[] {
  return DISCIPLINES.flatMap((d) => masteriesOf(d).slice(0, p.mastery?.[d] ?? 0));
}

/** The title a player goes by: their highest tier, the first Discipline winning a tie. */
export function bestTitle(p: Pick<PlayerState, 'mastery'>): string | null {
  let best: Mastery | null = null;
  for (const m of earned(p)) if (!best || m.tier > best.tier) best = m;
  return best?.title ?? null;
}

type Effect<K extends PassiveEffect['kind']> = Extract<PassiveEffect, { kind: K }>;

/** Passives of one kind the player holds, optionally only from one Discipline. */
function effects<K extends PassiveEffect['kind']>(p: Pick<PlayerState, 'mastery'>, kind: K, discipline?: Discipline): Effect<K>[] {
  return earned(p)
    .filter((m) => m.passive.effect.kind === kind && (!discipline || m.discipline === discipline))
    .map((m) => m.passive.effect as Effect<K>);
}

/** Added to a bar's max (Mood: its base). */
export const barBonus = (p: Pick<PlayerState, 'mastery'>, bar: Effect<'barMax'>['bar']) =>
  effects(p, 'barMax').reduce((n, e) => n + (e.bar === bar ? e.amount : 0), 0);
/** Extra XP, as a fraction, for a skill in this Discipline. */
export const xpBonus = (p: Pick<PlayerState, 'mastery'>, d: Discipline) => effects(p, 'xp', d).reduce((n, e) => n + e.bonus, 0);
/** Multiplier on a combat stat in a fight. */
export const fightStatMult = (p: Pick<PlayerState, 'mastery'>, stat: Stat) =>
  1 + effects(p, 'fightStats').reduce((n, e) => n + (e.stats.includes(stat) ? e.bonus : 0), 0);
/** Vigour taken off the cost of an attack. */
export const attackCostCut = (p: Pick<PlayerState, 'mastery'>) => effects(p, 'attackCost').reduce((n, e) => n + e.amount, 0);
/** Added to the quality score of graded goods made at this Discipline's venues. */
export const qualityBonus = (p: Pick<PlayerState, 'mastery'>, d: Discipline) => effects(p, 'quality', d).reduce((n, e) => n + e.bonus, 0);
/** Chance a craft keeps its materials, from mastery alone (the Artisan's Thrift is added by the craft). */
export const masteryThrift = (p: Pick<PlayerState, 'mastery'>) => effects(p, 'thrift').reduce((n, e) => n + e.chance, 0);
/** Chance a craft keeps its materials: the Artisan's Thrift plus the Craft mastery. */
export const craftThrift = (p: Pick<PlayerState, 'calling' | 'mastery'>) => (p.calling === 'artisan' ? balance.perks.artisanThrift : 0) + masteryThrift(p);
/** Taken off the market fee. */
export const feeCut = (p: Pick<PlayerState, 'mastery'>) => effects(p, 'marketFee').reduce((n, e) => n + e.reduction, 0);
/** Multiplier on time in gaol or the Infirmary. */
export const timeMult = (p: Pick<PlayerState, 'mastery'>, kind: 'gaolTime' | 'infirmaryTime') => effects(p, kind).reduce((n, e) => n * e.mult, 1);
/** Multiplier on what food, drink and draughts do. */
export const consumableMult = (p: Pick<PlayerState, 'mastery'>) => 1 + effects(p, 'consumables').reduce((n, e) => n + e.bonus, 0);

/** One event for each title newly earned. */
export function masteryEvents(before: MasteryTiers | undefined, after: MasteryTiers): GameEvent[] {
  return DISCIPLINES.flatMap((d) =>
    masteriesOf(d)
      .slice(before?.[d] ?? 0, after[d] ?? 0)
      .map((m) => ({ type: 'mastery', text: `You are now known as ${m.title}. ${m.passive.description}`, data: { discipline: d, tier: m.tier } })),
  );
}
